import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useInfiniteList, useAutoLoad } from "@/hooks/useInfiniteList";
import { useAuth } from "@/context/AuthContext";
import { canManage } from "@/lib/perms";
import { StatusBadge } from "@/components/Badges";
import { label, money } from "@/lib/constants";
import { todayIso } from "@/lib/dates";
import { useWorkMeta } from "@/lib/workMeta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import MonthCalendar from "@/components/MonthCalendar";
import { Plus, Loader2, CalendarDays, Clock, MapPin, UserCheck, List, Repeat } from "lucide-react";

const STATUSES = ["all", "pending", "assigned", "in_progress", "awaiting_inspection", "completed", "closed"];
const REPEATS = [
  { v: "none", t: "Does not repeat" },
  { v: "weekly", t: "Weekly" },
  { v: "biweekly", t: "Every 2 weeks" },
  { v: "monthly", t: "Every 4 weeks" },
];

export default function Schedule() {
  const { user } = useAuth();
  const meta = useWorkMeta();
  const [properties, setProperties] = useState([]);
  const [cleaners, setCleaners] = useState([]);
  const [filter, setFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [view, setView] = useState("list");
  const [month, setMonth] = useState(new Date());
  const [searchParams, setSearchParams] = useSearchParams();
  const todayOnly = searchParams.get("date") === "today";
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    property_id: "", cleaner_id: "", cleaner_ids: [], work_type: "turnover", title: "", cost: 0,
    scheduled_date: todayIso(), scheduled_time: "11:00",
    duration_hours: 3, priority: "normal", notes: "", repeat: "none", repeat_count: 4,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const monthKey = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, "0")}`;
  const listParams = useMemo(() => {
    const p = {};
    if (filter !== "all") p.status = filter;
    if (typeFilter !== "all") p.work_type = typeFilter;
    if (todayOnly) {
      const today = todayIso();
      p.from_date = today;
      p.to_date = today;
    }
    return p;
  }, [filter, typeFilter, todayOnly]);

  const { items: jobs, loadMore, hasMore, loading, reload } =
    useInfiniteList("/jobs", { params: listParams, pageSize: 100 });
  const sentinel = useAutoLoad(loadMore, hasMore && view === "list");
  const [monthJobs, setMonthJobs] = useState([]);

  const load = reload;

  useEffect(() => {
    api.get("/properties").then(({ data }) => setProperties(data));
    if (canManage(user)) api.get("/users", { params: { role: "assignable" } }).then(({ data }) => setCleaners(data));
  }, [user]);

  // The calendar needs a whole month at once, which is a different question from the list.
  useEffect(() => {
    if (view !== "month") return;
    const last = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    api.get("/jobs", { params: { from_date: `${monthKey}-01`, to_date: `${monthKey}-${last}`,
                                page_size: 500 } })
      .then(({ data }) => setMonthJobs(data));
  }, [view, monthKey, month]);

  useEffect(() => {
    const status = searchParams.get("status");
    setFilter(status && STATUSES.includes(status) ? status : "all");
    setView(searchParams.get("view") === "month" ? "month" : "list");
  }, [searchParams]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/jobs", {
        ...form,
        cleaner_id: form.cleaner_ids?.[0] || form.cleaner_id || null,
        cleaner_ids: form.cleaner_ids?.length ? form.cleaner_ids : (form.cleaner_id ? [form.cleaner_id] : []),
        duration_hours: Number(form.duration_hours),
        cost: Number(form.cost || 0),
        repeat_count: form.repeat === "none" ? 1 : Number(form.repeat_count),
        checklist: [],
      });
      toast.success(data?.created ? `${data.created} recurring work orders scheduled` : "Work order created");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create work order");
    }
    setBusy(false);
  };

  const assign = async (jobId, cleanerId) => {
    try {
      await api.put(`/jobs/${jobId}`, { cleaner_id: cleanerId });
      toast.success("Assigned");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not assign");
    }
  };

  // The server already applied status, work type and the today window (see listParams).
  const scoped = jobs;
  const grouped = scoped.reduce((acc, j) => {
    (acc[j.scheduled_date] = acc[j.scheduled_date] || []).push(j);
    return acc;
  }, {});
  const dates = Object.keys(grouped).sort();

  return (
    <div className="space-y-6" data-testid="schedule-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Operations</p>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-royal-deep mt-2">Work Orders</h1>
          <p className="text-muted-foreground mt-3 text-sm md:text-base">
            Cleaning, turnovers, inspections, maintenance, repairs, restocking and vendor work — one queue.
          </p>
        </div>
        {canManage(user) && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="schedule-job-btn" className="bg-royal hover:bg-royal-deep text-white gap-2">
                <Plus className="h-4 w-4" /> New work order
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[88vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display text-royal-deep">New work order</DialogTitle>
                <DialogDescription>Pick the type of work, the property, who is doing it and when.</DialogDescription>
              </DialogHeader>
              <form onSubmit={create} className="space-y-4" data-testid="job-form">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Property</Label>
                  <Select value={form.property_id} onValueChange={(v) => set("property_id", v)}>
                    <SelectTrigger data-testid="job-property-select" className="mt-1.5"><SelectValue placeholder="Select property" /></SelectTrigger>
                    <SelectContent>
                      {properties.map((p) => (
                        <SelectItem key={p._id} value={p._id} data-testid={`job-property-option-${p._id}`}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Assign to ({form.cleaner_ids.length} selected)
                  </Label>
                  <div className="mt-1.5 max-h-36 space-y-1 overflow-y-auto rounded-md border border-border p-2"
                       data-testid="job-crew-list">
                    {cleaners.map((c) => {
                      const on = form.cleaner_ids.includes(c._id);
                      return (
                        <label key={c._id} data-testid={`job-crew-${c._id}`}
                               className="flex cursor-pointer items-center gap-2.5 rounded px-1.5 py-1 hover:bg-royal/5">
                          <input type="checkbox" checked={on} className="h-4 w-4 accent-[#1b2a63]"
                                 onChange={() => set("cleaner_ids", on
                                   ? form.cleaner_ids.filter((x) => x !== c._id)
                                   : [...form.cleaner_ids, c._id])} />
                          <span className="text-sm text-royal-deep">{c.name}</span>
                          {on && form.cleaner_ids[0] === c._id && (
                            <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider text-gold">Lead</span>
                          )}
                        </label>
                      );
                    })}
                    {!cleaners.length && <p className="text-xs text-muted-foreground">No assignable people yet.</p>}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Work type</Label>
                    <Select value={form.work_type} onValueChange={(v) => set("work_type", v)}>
                      <SelectTrigger data-testid="job-type-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(meta?.work_types || []).map((t) => (
                          <SelectItem key={t.value} value={t.value} data-testid={`work-type-option-${t.value}`}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Priority</Label>
                    <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                      <SelectTrigger data-testid="job-priority-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["low", "normal", "high", "urgent"].map((p) => (
                          <SelectItem key={p} value={p}>{label(p)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Date</Label>
                    <Input data-testid="job-date-input" type="date" required value={form.scheduled_date} onChange={(e) => set("scheduled_date", e.target.value)} className="mt-1.5" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Start time</Label>
                    <Input data-testid="job-time-input" type="time" value={form.scheduled_time} onChange={(e) => set("scheduled_time", e.target.value)} className="mt-1.5" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                    <Repeat className="h-3.5 w-3.5 text-gold" /> Repeat
                  </Label>
                  <div className="grid grid-cols-2 gap-4 mt-1.5">
                    <Select value={form.repeat} onValueChange={(v) => set("repeat", v)}>
                      <SelectTrigger data-testid="job-repeat-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {REPEATS.map((r) => (
                          <SelectItem key={r.v} value={r.v} data-testid={`repeat-option-${r.v}`}>{r.t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      data-testid="job-repeat-count-input"
                      type="number"
                      min="1"
                      max="52"
                      disabled={form.repeat === "none"}
                      value={form.repeat_count}
                      onChange={(e) => set("repeat_count", e.target.value)}
                      placeholder="Occurrences"
                    />
                  </div>
                  {form.repeat !== "none" && (
                    <p className="text-[11px] text-muted-foreground mt-1.5">
                      Creates {form.repeat_count} jobs starting {form.scheduled_date}.
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Title</Label>
                    <Input data-testid="job-title-input" value={form.title}
                      onChange={(e) => set("title", e.target.value)} placeholder="Defaults to the work type"
                      className="mt-1.5" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Cost ($)</Label>
                    <Input data-testid="job-cost-input" type="number" min="0" step="0.01" value={form.cost}
                      onChange={(e) => set("cost", e.target.value)} className="mt-1.5" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Notes for the crew</Label>
                  <Textarea data-testid="job-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} className="mt-1.5" />
                </div>
                <Button data-testid="save-job-btn" type="submit" disabled={busy || !form.property_id} className="w-full bg-royal hover:bg-royal-deep text-white">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create work order"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {todayOnly && (
        <div className="flex items-center gap-3 rounded-lg border border-gold/40 bg-gold/10 px-4 py-2.5" data-testid="today-filter-banner">
          <CalendarDays className="h-4 w-4 text-gold" />
          <p className="text-sm text-royal-deep">Showing <span className="font-semibold">today's</span> jobs only</p>
          <button
            data-testid="clear-today-filter"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete("date");
              setSearchParams(next);
            }}
            className="ml-auto text-xs font-semibold text-royal hover:text-gold transition-colors duration-200"
          >
            Show all dates
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-2 flex-wrap" data-testid="status-filters">
          {STATUSES.map((s) => (
            <button
              key={s}
              data-testid={`filter-${s}`}
              onClick={() => {
                setFilter(s);
                const next = new URLSearchParams(searchParams);
                if (s === "all") next.delete("status");
                else next.set("status", s);
                setSearchParams(next);
              }}
              className={`rounded-full border px-4 min-h-[40px] text-xs font-medium touch-manipulation select-none transition-colors duration-200 ${
                filter === s ? "bg-royal text-white border-royal" : "bg-white text-royal border-border hover:border-royal/40"
              }`}
            >
              {label(s)}
            </button>
          ))}
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger data-testid="work-type-filter" className="w-[190px] h-10 text-xs">
            <SelectValue placeholder="All work types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All work types</SelectItem>
            {(meta?.work_types || []).map((t) => (
              <SelectItem key={t.value} value={t.value} data-testid={`filter-type-${t.value}`}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="ml-auto flex rounded-md border border-border bg-white overflow-hidden" data-testid="view-toggle">
          <button
            data-testid="view-list-btn"
            onClick={() => {
              setView("list");
              const next = new URLSearchParams(searchParams);
              next.delete("view");
              setSearchParams(next);
            }}
            className={`flex items-center gap-1.5 px-4 min-h-[40px] text-xs font-medium touch-manipulation select-none transition-colors duration-200 ${
              view === "list" ? "bg-royal text-white" : "text-royal hover:bg-royal/5 active:bg-royal/10"
            }`}
          >
            <List className="h-3.5 w-3.5" /> List
          </button>
          <button
            data-testid="view-month-btn"
            onClick={() => {
              setView("month");
              const next = new URLSearchParams(searchParams);
              next.set("view", "month");
              setSearchParams(next);
            }}
            className={`flex items-center gap-1.5 px-4 min-h-[40px] text-xs font-medium touch-manipulation select-none transition-colors duration-200 ${
              view === "month" ? "bg-royal text-white" : "text-royal hover:bg-royal/5 active:bg-royal/10"
            }`}
          >
            <CalendarDays className="h-3.5 w-3.5" /> Month
          </button>
        </div>
      </div>

      {view === "month" ? (
        <MonthCalendar jobs={monthJobs} month={month} onMonthChange={setMonth} />
      ) : (
      <div className="space-y-8" data-testid="jobs-list">
        {dates.length === 0 && <p className="text-sm text-muted-foreground">No work orders match this filter.</p>}
        {dates.map((d) => (
          <section key={d}>
            <div className="flex items-center gap-3 mb-3">
              <CalendarDays className="h-4 w-4 text-gold" />
              <h2 className="font-display text-base md:text-lg font-semibold text-royal-deep">
                {new Date(d + "T12:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
              </h2>
              <div className="h-px flex-1 bg-border" />
            </div>
            <div className="space-y-3">
              {grouped[d].map((j) => (
                <div
                  key={j._id}
                  data-testid={`job-row-${j._id}`}
                  className="rounded-lg border border-border bg-white p-4 sm:p-5 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-4 hover:shadow-md transition-shadow duration-200"
                >
                  <div className="min-w-0 sm:flex-1 basis-full sm:basis-auto">
                    <Link to={`/jobs/${j._id}`} data-testid={`job-link-${j._id}`} className="font-display text-base font-semibold text-royal-deep hover:text-gold transition-colors duration-200 break-words">
                      {j.title}
                    </Link>
                    <p className="text-xs text-muted-foreground mt-0.5">{j.property_name}</p>
                    <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-x-4 gap-y-1 mt-1.5 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 shrink-0" /> {j.scheduled_time} · {j.duration_hours}h</span>
                      <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{j.property_address}</span></span>
                      <span className="flex items-center gap-1.5"><UserCheck className="h-3.5 w-3.5 shrink-0" /> {(j.crew_names || []).join(", ") || "Unassigned"}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                    <span className="text-[11px] rounded-full bg-royal/5 border border-royal/10 px-2 py-0.5 text-royal">
                      {j.work_type_label}
                    </span>
                    {j.priority !== "normal" && (
                      <span className="text-[11px] rounded-full bg-gold/20 border border-gold/40 px-2 py-0.5 text-royal-deep font-semibold">
                        {label(j.priority)}
                      </span>
                    )}
                    {j.cost > 0 && <span className="text-[11px] text-muted-foreground">{money(j.cost)}</span>}
                    <Link
                      to={`/properties/${j.property_id}`}
                      data-testid={`job-property-link-${j._id}`}
                      className="inline-flex items-center min-h-[36px] text-xs font-semibold text-royal hover:text-gold touch-manipulation transition-colors duration-200"
                    >
                      Property →
                    </Link>
                    <StatusBadge value={j.status} testId={`job-status-${j._id}`} />
                  </div>
                  {canManage(user) && (
                    <Select value={j.cleaner_id || "none"} onValueChange={(v) => v !== "none" && assign(j._id, v)}>
                      <SelectTrigger data-testid={`assign-cleaner-${j._id}`} className="w-full sm:w-[180px] h-11 text-xs shrink-0">
                        <SelectValue placeholder="Assign" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Unassigned</SelectItem>
                        {cleaners.map((c) => (
                          <SelectItem key={c._id} value={c._id} data-testid={`assign-option-${j._id}-${c._id}`}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              ))}
            </div>
          </section>
        ))}
        <div ref={sentinel} />
        {loading && (
          <p data-testid="jobs-loading-more" className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading more work orders…
          </p>
        )}
        {!hasMore && dates.length > 0 && (
          <p data-testid="jobs-list-end" className="text-xs text-muted-foreground">
            That's every work order matching this filter.
          </p>
        )}
      </div>
      )}
    </div>
  );
}
